import { c as _c } from "react/compiler-runtime";
// Раздел «Комиссии» (встреча 25.09.2026, эскиз Элины).
//
// Раньше ставки жили карточкой внутри «Финансов»: базовая, категории и
// магазины выпадашками. Заказчику нужен рабочий инструмент, и основной формат
// здесь — таблица:
//
//   • базовая ставка площадки — сверху;
//   • категории деревом: своя ставка правится прямо в строке, подкатегория без
//     своей ставки пишет «наследует N %»; клик по категории раскрывает её
//     магазины, где ставится персональная ставка магазину в этой категории;
//   • магазины с поиском и фильтрами: строка раскрывается в категории магазина
//     со ставкой по каждой; чекбоксы и «Выставить ставку» — сразу нескольким;
//   • история изменений из журнала действий.
//
// Каждая ячейка ставки — самостоятельная: сохраняется Enter-ом или уходом
// фокуса, поэтому с двумя-тремя магазинами работают одновременно, ничего не
// закрывая и не открывая.
import { ChevronDown, ChevronRight, History, Loader2, Percent, Search, SlidersHorizontal, Store, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CommissionCategoryRow, CommissionShopRow, CommissionSource } from "@/api";
import { useCommissionCategoryShops, useCommissionHistory, useCommissionMutations, useCommissionOverview, useCommissionShops } from "@/api/hooks/use-commissions";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Card, CardContent } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { formatCurrency, formatDateish } from "@osmix/formatters";
const money = (value: number) => formatCurrency(value, {
  maximumFractionDigits: 0
});
const pctFormat = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 2
});
/** 0.065 → «6,5 %». */
const pct = (rate: number | null | undefined) => rate == null ? "—" : `${pctFormat.format(Math.round(rate * 100_000) / 1000)} %`;

/** «6,5» / «6.5» → 0.065; пусто → null; мусор → undefined. */
const parsePct = (text: string): number | null | undefined => {
  const clean = text.replace(",", ".").replace("%", "").trim();
  if (!clean) return null;
  const value = Number(clean);
  if (!Number.isFinite(value) || value < 0 || value > 100) return undefined;
  return Math.round(value * 1000) / 100_000;
};
const PERIODS = [7, 30, 90, 365] as const;

// ── Сворачиваемая секция ───────────────────────────────────────────────────

const SECTIONS_KEY = "osmix.admin.commissions.sections";
const readSections = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
};
function Section(t0) {
  const $ = _c(31);
  const {
    id,
    title,
    hint,
    icon: Icon,
    defaultOpen: t1,
    actions,
    children
  } = t0;
  const defaultOpen = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== defaultOpen || $[1] !== id) {
    t2 = () => readSections()[id] ?? defaultOpen;
    $[0] = defaultOpen;
    $[1] = id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [open, setOpen] = useState(t2);
  let t3;
  if ($[3] !== id || $[4] !== open) {
    t3 = () => {
      const next = !open;
      setOpen(next);
      try {
        localStorage.setItem(SECTIONS_KEY, JSON.stringify({
          ...readSections(),
          [id]: next
        }));
      } catch {}
    };
    $[3] = id;
    $[4] = open;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const toggle = t3;
  const t4 = `commissions-${id}`;
  let t5;
  if ($[6] !== open) {
    t5 = open ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />;
    $[6] = open;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== Icon) {
    t6 = <Icon className="size-4 shrink-0 text-muted-foreground" />;
    $[8] = Icon;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== title) {
    t7 = <span className="font-semibold">{title}</span>;
    $[10] = title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== hint) {
    t8 = hint && <span className="hidden truncate text-sm text-muted-foreground md:inline">{hint}</span>;
    $[12] = hint;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== open || $[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8 || $[19] !== toggle) {
    t9 = <button type="button" onClick={toggle} aria-expanded={open} className="flex min-w-0 items-center gap-2 text-left">{t5}{t6}{t7}{t8}</button>;
    $[14] = open;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = toggle;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = open && actions;
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== children || $[25] !== open) {
    t12 = open && <CardContent className="border-t px-4 py-4 sm:px-6">{children}</CardContent>;
    $[24] = children;
    $[25] = open;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t4) {
    t13 = <Card className="gap-0 py-0" data-tour={t4}>{t11}{t12}</Card>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t4;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}

// ── Ячейка ставки ──────────────────────────────────────────────────────────

/**
 * Ставка прямо в строке таблицы. Пустое поле с подсказкой «наследует N %» —
 * своей ставки нет; ввод и Enter (или уход фокуса) сохраняют, крестик снимает.
 */
function RateCell(t0) {
  const $ = _c(46);
  const {
    value,
    inherited,
    onSave,
    disabled,
    label,
    clearable: t1
  } = t0;
  const clearable = t1 === undefined ? true : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== value) {
    t2 = value == null ? "" : pctFormat.format(Math.round(value * 100000) / 1000);
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const shown = t2;
  const [text, setText] = useState(shown);
  const [state, setState] = useState("idle");
  let t3;
  let t4;
  if ($[2] !== shown) {
    t3 = () => setText(shown);
    t4 = [shown];
    $[2] = shown;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== onSave || $[6] !== shown || $[7] !== value) {
    t5 = async next => {
      const parsed = parsePct(next);
      if (parsed === undefined) {
        setState("error");
        return;
      }
      if (parsed === value) {
        setState("idle");
        setText(shown);
        return;
      }
      setState("saving");
      try {
        await onSave(parsed);
        setState("idle");
      } catch {
        setState("error");
      }
    };
    $[5] = onSave;
    $[6] = shown;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const commit = t5;
  if (disabled) {
    let t6;
    if ($[9] !== inherited || $[10] !== value) {
      t6 = value != null ? pct(value) : <span className="text-muted-foreground">{inherited}</span>;
      $[9] = inherited;
      $[10] = value;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== t6) {
      t7 = <span className="tabular-nums">{t6}</span>;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  const t6 = state === "error" && "border-destructive focus-visible:ring-destructive/40";
  let t7;
  if ($[14] !== t6) {
    t7 = cn("h-8 w-20 pr-6 text-right tabular-nums", t6);
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== state) {
    t8 = e => {
      setText(e.target.value);
      if (state === "error") {
        setState("idle");
      }
    };
    $[16] = state;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== commit || $[19] !== text) {
    t9 = () => void commit(text);
    $[18] = commit;
    $[19] = text;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== shown) {
    t10 = e_0 => {
      if (e_0.key === "Enter") {
        (e_0.target as HTMLInputElement).blur();
      }
      if (e_0.key === "Escape") {
        setText(shown);
        setState("idle");
      }
    };
    $[21] = shown;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== label || $[24] !== t10 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9 || $[28] !== text) {
    t11 = <Input aria-label={label} inputMode="decimal" className={t7} value={text} placeholder={"\u2014"} onChange={t8} onBlur={t9} onKeyDown={t10} />;
    $[23] = label;
    $[24] = t10;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = text;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-muted-foreground">%</span>;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11) {
    t13 = <div className="relative">{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== clearable || $[34] !== commit || $[35] !== state || $[36] !== t || $[37] !== value) {
    t14 = state === "saving" ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : value != null && clearable ? <Button size="icon-xs" variant="ghost" aria-label={t("commissions.clearRate")} title={t("commissions.clearRate")} onClick={() => {
      setText("");
      commit("");
    }}><X /></Button> : <span className="size-6" />;
    $[33] = clearable;
    $[34] = commit;
    $[35] = state;
    $[36] = t;
    $[37] = value;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== inherited || $[40] !== value) {
    t15 = value == null && inherited && <span className="text-xs whitespace-nowrap text-muted-foreground">{inherited}</span>;
    $[39] = inherited;
    $[40] = value;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="flex items-center gap-1">{t13}{t14}{t15}</div>;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}
function SourceBadge(t0) {
  const $ = _c(7);
  const {
    source,
    categoryName
  } = t0;
  const {
    t
  } = useTranslation();
  const variant = source === "shop_category" ? "default" : source === "shop" ? "secondary" : "outline";
  const t1 = `commissions.source.${source}`;
  let t2;
  if ($[0] !== t || $[1] !== t1) {
    t2 = t(t1);
    $[0] = t;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = categoryName && source !== "shop" && source !== "base" ? ` · ${categoryName}` : "";
  let t4;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== variant) {
    t4 = <Badge variant={variant} className="whitespace-nowrap">{t2}{t3}</Badge>;
    $[3] = t2;
    $[4] = t3;
    $[5] = variant;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// ── Страница ───────────────────────────────────────────────────────────────

export function CommissionsPage() {
  const $ = _c(74);
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.finances.manage");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canEdit = t0;
  const [days, setDays] = useState(30);
  const overview = useCommissionOverview(days);
  const shops = useCommissionShops(days);
  let map;
  if ($[2] !== overview.data?.categories) {
    map = new Map();
    for (const row of overview.data?.categories ?? []) {
      map.set(row.id, row.name);
    }
    $[2] = overview.data?.categories;
    $[3] = map;
  } else {
    map = $[3];
  }
  const categoryName = map;
  let t1;
  if ($[4] !== t) {
    t1 = t("commissions.title");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <h1 className="text-2xl font-bold tracking-tight">{t1}</h1>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = t("commissions.description");
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <p className="max-w-3xl text-sm text-muted-foreground">{t3}</p>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div data-tour="commissions-intro">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("commissions.period");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <span className="text-muted-foreground">{t6}</span>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = String(days);
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e => setDays(Number(e.target.value));
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = PERIODS.map(value => <option key={value} value={value}>{t("commissions.periodDays", {
        count: value
      })}</option>);
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <Select value={t8} onChange={t9}>{t10}</Select>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t7) {
    t12 = <label className="flex items-center gap-2 text-sm" data-tour="commissions-period">{t7}{t11}</label>;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t5) {
    t13 = <div className="flex flex-wrap items-start justify-between gap-4">{t5}{t12}</div>;
    $[28] = t12;
    $[29] = t5;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== overview.isError || $[32] !== t) {
    t14 = overview.isError && <p className="text-sm text-destructive">{t("commissions.loadError")}</p>;
    $[31] = overview.isError;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = overview.data?.baseRate;
  let t16;
  if ($[34] !== canEdit || $[35] !== t15) {
    t16 = <BaseRateSection baseRate={t15} canEdit={canEdit} />;
    $[34] = canEdit;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("commissions.categories.title");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("commissions.categories.hint");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== canEdit || $[42] !== days || $[43] !== overview.data) {
    t19 = overview.data ? <CategoryTable rows={overview.data.categories} baseRate={overview.data.baseRate} days={days} canEdit={canEdit} /> : <Loading />;
    $[41] = canEdit;
    $[42] = days;
    $[43] = overview.data;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19) {
    t20 = <Section id="categories" icon={Percent} title={t17} hint={t18}>{t19}</Section>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t) {
    t21 = t("commissions.shops.title");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t) {
    t22 = t("commissions.shops.hint");
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== canEdit || $[54] !== categoryName || $[55] !== overview.data || $[56] !== shops.data) {
    t23 = shops.data && overview.data ? <ShopsBlock shops={shops.data.shops} categories={overview.data.categories} canEdit={canEdit} categoryName={categoryName} /> : <Loading />;
    $[53] = canEdit;
    $[54] = categoryName;
    $[55] = overview.data;
    $[56] = shops.data;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t21 || $[59] !== t22 || $[60] !== t23) {
    t24 = <Section id="shops" icon={Store} title={t21} hint={t22}>{t23}</Section>;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t) {
    t25 = t("commissions.history.title");
    $[62] = t;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <HistoryTable />;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t25) {
    t27 = <Section id="history" icon={History} title={t25} defaultOpen={false}>{t26}</Section>;
    $[65] = t25;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== t13 || $[68] !== t14 || $[69] !== t16 || $[70] !== t20 || $[71] !== t24 || $[72] !== t27) {
    t28 = <div className="flex flex-col gap-4">{t13}{t14}{t16}{t20}{t24}{t27}</div>;
    $[67] = t13;
    $[68] = t14;
    $[69] = t16;
    $[70] = t20;
    $[71] = t24;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}
function _temp(s) {
  return s.user;
}
function Loading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ── Базовая ставка ─────────────────────────────────────────────────────────

function BaseRateSection(t0) {
  const $ = _c(24);
  const {
    baseRate,
    canEdit
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    setBase
  } = useCommissionMutations();
  let t1;
  if ($[0] !== t) {
    t1 = t("commissions.base.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("commissions.base.label");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-sm">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== baseRate || $[7] !== canEdit || $[8] !== setBase || $[9] !== t) {
    t4 = baseRate == null ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : <RateCell label={t("commissions.base.label")} value={baseRate} clearable={false} disabled={!canEdit} onSave={async rate => {
      if (rate == null || rate <= 0) {
        throw new Error("base rate required");
      }
      await setBase.mutateAsync(rate);
    }} />;
    $[6] = baseRate;
    $[7] = canEdit;
    $[8] = setBase;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex flex-wrap items-center gap-3">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("commissions.base.priority");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <p className="text-xs text-muted-foreground">{t6}</p>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7) {
    t8 = <div className="flex flex-col gap-3">{t5}{t7}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t1 || $[22] !== t8) {
    t9 = <Section id="base" icon={Percent} title={t1}>{t8}</Section>;
    $[21] = t1;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}

// ── Категории ──────────────────────────────────────────────────────────────

function CategoryTable({
  rows,
  baseRate,
  days,
  canEdit
}: {
  rows: CommissionCategoryRow[];
  baseRate: number;
  days: number;
  canEdit: boolean;
}) {
  const {
    t
  } = useTranslation();
  const {
    setRate
  } = useCommissionMutations();
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [openShops, setOpenShops] = useState<string | null>(null);
  const [adding, setAdding] = useState<{
    categoryId: string;
    pct: string;
  } | null>(null);
  const [addError, setAddError] = useState(false);
  const hasChildren = useMemo(() => new Set(rows.map(row => row.parentId).filter(Boolean)), [rows]);
  const names = useMemo(() => new Map(rows.map(row_0 => [row_0.id, row_0.name])), [rows]);
  const parentOf = useMemo(() => new Map(rows.map(row_1 => [row_1.id, row_1.parentId])), [rows]);
  const hidden = (row_2: CommissionCategoryRow) => {
    let parent = row_2.parentId;
    while (parent) {
      if (collapsed.has(parent)) return true;
      parent = parentOf.get(parent) ?? null;
    }
    return false;
  };
  const toggle = (id: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  });
  const inheritedLabel = (row_3: CommissionCategoryRow) => row_3.inheritedFromId ? t("commissions.inherits", {
    rate: pct(row_3.effectiveRate),
    from: names.get(row_3.inheritedFromId) ?? ""
  }) : t("commissions.inheritsBase", {
    rate: pct(baseRate)
  });
  const withoutRate = rows.filter(row_4 => row_4.rate == null);
  const add = async () => {
    if (!adding) return;
    const rate = parsePct(adding.pct);
    if (!adding.categoryId || rate == null || rate === undefined) {
      setAddError(true);
      return;
    }
    await setRate.mutateAsync({
      categoryId: adding.categoryId,
      rate
    });
    setAdding(null);
    setAddError(false);
  };
  return <div className="flex flex-col gap-3">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="min-w-56">{t("commissions.categories.name")}</TableHead>
						<TableHead className="min-w-44">{t("commissions.categories.rate")}</TableHead>
						<TableHead className="text-right">{t("commissions.categories.shops")}</TableHead>
						<TableHead className="text-right">{t("commissions.categories.personal")}</TableHead>
						<TableHead className="text-right">{t("commissions.categories.sales")}</TableHead>
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.filter(row_5 => !hidden(row_5)).map(row_6 => <CategoryRowView key={row_6.id} row={row_6} days={days} canEdit={canEdit} expandable={hasChildren.has(row_6.id)} collapsed={collapsed.has(row_6.id)} onToggle={() => toggle(row_6.id)} shopsOpen={openShops === row_6.id} onShops={() => setOpenShops(openShops === row_6.id ? null : row_6.id)} inherited={inheritedLabel(row_6)} names={names} onSave={rate_0 => setRate.mutateAsync({
          categoryId: row_6.id,
          rate: rate_0
        })} />)}
				</TableBody>
			</Table>

			{canEdit && (adding ? <div className="flex flex-wrap items-center gap-2">
						<Select className="w-72" value={adding.categoryId} onChange={e => setAdding({
        ...adding,
        categoryId: e.target.value
      })}>
							<option value="">{t("commissions.categories.choose")}</option>
							{withoutRate.map(row_7 => <option key={row_7.id} value={row_7.id}>
									{`${"— ".repeat(row_7.depth)}${row_7.name}`}
								</option>)}
						</Select>
						<div className="relative">
							<Input aria-label={t("commissions.categories.rate")} inputMode="decimal" className={cn("h-9 w-24 pr-6 text-right", addError && "border-destructive")} value={adding.pct} onChange={e_0 => setAdding({
          ...adding,
          pct: e_0.target.value
        })} onKeyDown={e_1 => e_1.key === "Enter" && void add()} />
							<span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-muted-foreground">
								%
							</span>
						</div>
						<Button size="sm" disabled={setRate.isPending} onClick={() => void add()}>
							{t("common.save", {
          defaultValue: "Сохранить"
        })}
						</Button>
						<Button size="sm" variant="ghost" onClick={() => setAdding(null)}>
							{t("common.cancel", {
          defaultValue: "Отмена"
        })}
						</Button>
					</div> : <div>
						<Button size="sm" variant="outline" onClick={() => setAdding({
        categoryId: "",
        pct: ""
      })}>
							{t("commissions.categories.add")}
						</Button>
					</div>)}
		</div>;
}
function CategoryRowView(t0) {
  const $ = _c(68);
  const {
    row,
    days,
    canEdit,
    expandable,
    collapsed,
    onToggle,
    shopsOpen,
    onShops,
    inherited,
    names,
    onSave
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = shopsOpen && "bg-muted/40";
  let t2;
  if ($[0] !== t1) {
    t2 = cn(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = row.depth * 20;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      paddingLeft: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== collapsed || $[5] !== expandable || $[6] !== onToggle || $[7] !== t) {
    t5 = expandable ? <Button size="icon-xs" variant="ghost" aria-label={t(collapsed ? "commissions.expand" : "commissions.collapse")} onClick={onToggle}>{collapsed ? <ChevronRight /> : <ChevronDown />}</Button> : <span className="size-6" />;
    $[4] = collapsed;
    $[5] = expandable;
    $[6] = onToggle;
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = row.depth === 0 && "font-medium";
  const t7 = shopsOpen && "underline";
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = cn("text-left hover:underline", t6, t7);
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t("commissions.categories.openShops");
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== onShops || $[15] !== row.name || $[16] !== t8 || $[17] !== t9) {
    t10 = <button type="button" onClick={onShops} className={t8} title={t9}>{row.name}</button>;
    $[14] = onShops;
    $[15] = row.name;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t4 || $[21] !== t5) {
    t11 = <TableCell><div className="flex items-center gap-1" style={t4}>{t5}{t10}</div></TableCell>;
    $[19] = t10;
    $[20] = t4;
    $[21] = t5;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== row.name || $[24] !== t) {
    t12 = t("commissions.categories.rateFor", {
      name: row.name
    });
    $[23] = row.name;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = !canEdit;
  let t14;
  if ($[26] !== inherited || $[27] !== onSave || $[28] !== row.rate || $[29] !== t12 || $[30] !== t13) {
    t14 = <TableCell><RateCell label={t12} value={row.rate} inherited={inherited} disabled={t13} onSave={onSave} /></TableCell>;
    $[26] = inherited;
    $[27] = onSave;
    $[28] = row.rate;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== row.shopCount) {
    t15 = <TableCell className="text-right tabular-nums">{row.shopCount}</TableCell>;
    $[32] = row.shopCount;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== onShops || $[35] !== row.personalCount) {
    t16 = <TableCell className="text-right tabular-nums">{row.personalCount > 0 ? <button type="button" className="font-medium hover:underline" onClick={onShops}>{row.personalCount}</button> : <span className="text-muted-foreground">—</span>}</TableCell>;
    $[34] = onShops;
    $[35] = row.personalCount;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== row.sales) {
    t17 = money(row.sales);
    $[37] = row.sales;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17) {
    t18 = <TableCell className="text-right tabular-nums">{t17}</TableCell>;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t("commissions.categories.openShops");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("commissions.categories.openShops");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Store />;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== onShops || $[47] !== t19 || $[48] !== t20) {
    t22 = <TableCell><Button size="icon-xs" variant="ghost" aria-label={t19} title={t20} onClick={onShops}>{t21}</Button></TableCell>;
    $[46] = onShops;
    $[47] = t19;
    $[48] = t20;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t11 || $[51] !== t14 || $[52] !== t15 || $[53] !== t16 || $[54] !== t18 || $[55] !== t2 || $[56] !== t22) {
    t23 = <TableRow className={t2}>{t11}{t14}{t15}{t16}{t18}{t22}</TableRow>;
    $[50] = t11;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
    $[54] = t18;
    $[55] = t2;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== canEdit || $[59] !== days || $[60] !== names || $[61] !== row.id || $[62] !== row.name || $[63] !== shopsOpen) {
    t24 = shopsOpen && <TableRow className="hover:bg-transparent"><TableCell colSpan={6} className="bg-muted/30 p-0"><CategoryShops categoryId={row.id} categoryName={row.name} days={days} canEdit={canEdit} names={names} /></TableCell></TableRow>;
    $[58] = canEdit;
    $[59] = days;
    $[60] = names;
    $[61] = row.id;
    $[62] = row.name;
    $[63] = shopsOpen;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t23 || $[66] !== t24) {
    t25 = <>{t23}{t24}</>;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  return t25;
}

/** Магазины категории: персональная ставка магазину именно в этой категории. */
function CategoryShops(t0) {
  const $ = _c(26);
  const {
    categoryId,
    categoryName,
    days,
    canEdit,
    names
  } = t0;
  const {
    t
  } = useTranslation();
  const query = useCommissionCategoryShops(categoryId, days);
  const {
    setRate
  } = useCommissionMutations();
  const [q, setQ] = useState("");
  if (!query.data) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loading />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== canEdit || $[2] !== categoryId || $[3] !== categoryName || $[4] !== names || $[5] !== q || $[6] !== query.data.shops || $[7] !== setRate || $[8] !== t) {
    const needle = q.trim().toLowerCase();
    const list = query.data.shops.filter(row => !needle || row.name.toLowerCase().includes(needle) || (row.inn ?? "").includes(needle));
    let t2;
    if ($[10] !== categoryName || $[11] !== query.data.shops.length || $[12] !== t) {
      t2 = t("commissions.categoryShops.title", {
        name: categoryName,
        count: query.data.shops.length
      });
      $[10] = categoryName;
      $[11] = query.data.shops.length;
      $[12] = t;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    let t3;
    if ($[14] !== t2) {
      t3 = <p className="text-sm font-medium">{t2}</p>;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t) {
      t5 = t("commissions.categoryShops.search");
      $[17] = t;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = e => setQ(e.target.value);
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== q || $[21] !== t5) {
      t7 = <div className="relative">{t4}<Input className="h-8 w-56 pl-8" placeholder={t5} value={q} onChange={t6} /></div>;
      $[20] = q;
      $[21] = t5;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== t3 || $[24] !== t7) {
      t8 = <div className="flex flex-wrap items-center justify-between gap-2">{t3}{t7}</div>;
      $[23] = t3;
      $[24] = t7;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    t1 = <div className="flex flex-col gap-2 px-3 py-3 sm:px-6">{t8}{list.length === 0 ? <p className="py-4 text-sm text-muted-foreground">{t("commissions.categoryShops.empty")}</p> : <Table><TableHeader><TableRow><TableHead>{t("commissions.shops.name")}</TableHead><TableHead>{t("commissions.shops.inn")}</TableHead><TableHead className="text-right">{t("commissions.shops.products")}</TableHead><TableHead className="text-right">{t("commissions.shops.sales")}</TableHead><TableHead className="min-w-40">{t("commissions.categoryShops.personal")}</TableHead><TableHead>{t("commissions.shops.effective")}</TableHead></TableRow></TableHeader><TableBody>{list.map(row_0 => <TableRow key={row_0.shopId}><TableCell className="font-medium">{row_0.name}</TableCell><TableCell className="text-muted-foreground tabular-nums">{row_0.inn ?? "\u2014"}</TableCell><TableCell className="text-right tabular-nums">{row_0.productCount}</TableCell><TableCell className="text-right tabular-nums">{money(row_0.sales)}</TableCell><TableCell><RateCell label={t("commissions.categoryShops.rateFor", {
                shop: row_0.name,
                category: categoryName
              })} value={row_0.rate} disabled={!canEdit} onSave={rate => setRate.mutateAsync({
                shopId: row_0.shopId,
                categoryId,
                rate
              })} /></TableCell><TableCell><div className="flex items-center gap-2"><span className="font-medium tabular-nums">{pct(row_0.effectiveRate)}</span><SourceBadge source={row_0.source} categoryName={row_0.sourceCategoryId ? names.get(row_0.sourceCategoryId) : undefined} /></div></TableCell></TableRow>)}</TableBody></Table>}</div>;
    $[1] = canEdit;
    $[2] = categoryId;
    $[3] = categoryName;
    $[4] = names;
    $[5] = q;
    $[6] = query.data.shops;
    $[7] = setRate;
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}

// ── Магазины ───────────────────────────────────────────────────────────────

type Filters = {
  categoryId: string;
  status: string;
  minSales: string;
  personal: "any" | "yes" | "no";
};
const EMPTY_FILTERS: Filters = {
  categoryId: "",
  status: "",
  minSales: "",
  personal: "any"
};
function ShopsBlock({
  shops,
  categories,
  canEdit,
  categoryName
}: {
  shops: CommissionShopRow[];
  categories: CommissionCategoryRow[];
  canEdit: boolean;
  categoryName: Map<string, string>;
}) {
  const {
    t
  } = useTranslation();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const lineage = useMemo(() => {
    const parent = new Map(categories.map(row => [row.id, row.parentId]));
    return (id: string) => {
      const out: string[] = [];
      let current: string | null = id;
      while (current && !out.includes(current)) {
        out.push(current);
        current = parent.get(current) ?? null;
      }
      return out;
    };
  }, [categories]);
  const statuses = useMemo(() => [...new Set(shops.map(s => s.status))].sort(), [shops]);
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const minSales = Number(filters.minSales.replace(/\s/g, "").replace(",", "."));
    return shops.filter(shop => {
      if (needle) {
        const hay = [shop.name, shop.inn ?? "", ...shop.categories.map(c => c.path)].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (filters.categoryId && !shop.categories.some(c_0 => lineage(c_0.categoryId).includes(filters.categoryId))) return false;
      if (filters.status && shop.status !== filters.status) return false;
      if (filters.minSales && Number.isFinite(minSales) && shop.sales < minSales) return false;
      const personal = shop.personalCount > 0 || shop.shopRate != null;
      if (filters.personal === "yes" && !personal) return false;
      if (filters.personal === "no" && personal) return false;
      return true;
    });
  }, [shops, q, filters, lineage]);
  const activeFilters = Number(!!filters.categoryId) + Number(!!filters.status) + Number(!!filters.minSales) + Number(filters.personal !== "any");
  const allVisibleSelected = visible.length > 0 && visible.every(shop_0 => selected.has(shop_0.id));
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allVisibleSelected) for (const shop_1 of visible) next.delete(shop_1.id);else for (const shop_2 of visible) next.add(shop_2.id);
    return next;
  });
  const toggle = (set: Set<string>, id_0: string) => {
    const next_0 = new Set(set);
    if (next_0.has(id_0)) next_0.delete(id_0);else next_0.add(id_0);
    return next_0;
  };
  return <div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<div className="relative min-w-0 flex-1 basis-64">
					<Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
					<Input className="pl-8" placeholder={t("commissions.shops.search")} value={q} onChange={e => setQ(e.target.value)} />
				</div>
				<Button size="sm" variant={showFilters ? "secondary" : "outline"} onClick={() => setShowFilters(!showFilters)}>
					<SlidersHorizontal />
					{t("commissions.filters.title")}
					{activeFilters > 0 && <Badge className="ml-1">{activeFilters}</Badge>}
				</Button>
			</div>

			<div className={cn("grid gap-4", showFilters && "2xl:grid-cols-[minmax(0,1fr)_15rem]")}>
				<div className="flex min-w-0 flex-col gap-3">
					{canEdit && selected.size > 0 && <BulkBar selected={[...selected]} names={new Map(shops.map(s_0 => [s_0.id, s_0.name]))} categories={categories} onClear={() => setSelected(new Set())} />}
					<p className="text-xs text-muted-foreground">
						{t("commissions.shops.found", {
            count: visible.length,
            total: shops.length
          })}
					</p>
					<Table>
						<TableHeader>
							<TableRow>
								{canEdit && <TableHead className="w-8">
										<input type="checkbox" className="size-4 accent-primary" aria-label={t("commissions.shops.selectAll")} checked={allVisibleSelected} onChange={toggleAll} />
									</TableHead>}
								<TableHead className="min-w-48">{t("commissions.shops.name")}</TableHead>
								<TableHead>{t("commissions.shops.inn")}</TableHead>
								<TableHead>{t("commissions.shops.status")}</TableHead>
								<TableHead className="text-right">{t("commissions.shops.products")}</TableHead>
								<TableHead className="text-right">{t("commissions.shops.sales")}</TableHead>
								<TableHead className="min-w-40">{t("commissions.shops.shopRate")}</TableHead>
								<TableHead className="text-right">{t("commissions.shops.personal")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{visible.length === 0 && <TableRow>
									<TableCell colSpan={canEdit ? 8 : 7} className="py-6 text-center text-muted-foreground">
										{t("commissions.shops.empty")}
									</TableCell>
								</TableRow>}
							{visible.map(shop_3 => <ShopRowView key={shop_3.id} shop={shop_3} canEdit={canEdit} expanded={expanded.has(shop_3.id)} onToggle={() => setExpanded(toggle(expanded, shop_3.id))} selected={selected.has(shop_3.id)} onSelect={() => setSelected(toggle(selected, shop_3.id))} categories={categories} categoryName={categoryName} />)}
						</TableBody>
					</Table>
				</div>

				{showFilters && <aside className="order-first grid gap-3 rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-4 2xl:order-none 2xl:flex 2xl:flex-col 2xl:self-start">
						<div className="flex items-center justify-between sm:col-span-2 lg:col-span-4">
							<p className="text-sm font-medium">{t("commissions.filters.title")}</p>
							{activeFilters > 0 && <Button size="xs" variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
									{t("commissions.filters.reset")}
								</Button>}
						</div>
						<label className="flex flex-col gap-1 text-sm">
							<span className="text-muted-foreground">{t("commissions.filters.category")}</span>
							<Select className="w-full" value={filters.categoryId} onChange={e_0 => setFilters({
            ...filters,
            categoryId: e_0.target.value
          })}>
								<option value="">{t("commissions.filters.any")}</option>
								{categories.map(row_0 => <option key={row_0.id} value={row_0.id}>
										{`${"— ".repeat(row_0.depth)}${row_0.name}`}
									</option>)}
							</Select>
						</label>
						<label className="flex flex-col gap-1 text-sm">
							<span className="text-muted-foreground">{t("commissions.filters.minSales")}</span>
							<Input inputMode="numeric" placeholder="0" value={filters.minSales} onChange={e_1 => setFilters({
            ...filters,
            minSales: e_1.target.value
          })} />
						</label>
						<label className="flex flex-col gap-1 text-sm">
							<span className="text-muted-foreground">{t("commissions.filters.status")}</span>
							<Select className="w-full" value={filters.status} onChange={e_2 => setFilters({
            ...filters,
            status: e_2.target.value
          })}>
								<option value="">{t("commissions.filters.any")}</option>
								{statuses.map(status => <option key={status} value={status}>
										{t(`commissions.status.${status}`, {
                defaultValue: status
              })}
									</option>)}
							</Select>
						</label>
						<label className="flex flex-col gap-1 text-sm">
							<span className="text-muted-foreground">{t("commissions.filters.personal")}</span>
							<Select className="w-full" value={filters.personal} onChange={e_3 => setFilters({
            ...filters,
            personal: e_3.target.value as Filters["personal"]
          })}>
								<option value="any">{t("commissions.filters.any")}</option>
								<option value="yes">{t("commissions.filters.personalYes")}</option>
								<option value="no">{t("commissions.filters.personalNo")}</option>
							</Select>
						</label>
					</aside>}
			</div>
		</div>;
}
function ShopRowView(t0) {
  const $ = _c(78);
  const {
    shop,
    canEdit,
    expanded,
    onToggle,
    selected,
    onSelect,
    categories,
    categoryName
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    setRate
  } = useCommissionMutations();
  const [adding, setAdding] = useState(null);
  const colSpan = canEdit ? 8 : 7;
  let t1;
  if ($[0] !== shop.categories) {
    t1 = new Set(shop.categories.map(_temp14));
    $[0] = shop.categories;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const own = t1;
  let t2;
  if ($[2] !== adding || $[3] !== setRate || $[4] !== shop.id) {
    t2 = async () => {
      if (!adding) {
        return;
      }
      const rate = parsePct(adding.pct);
      if (!adding.categoryId || rate == null || rate === undefined) {
        return;
      }
      await setRate.mutateAsync({
        shopId: shop.id,
        categoryId: adding.categoryId,
        rate
      });
      setAdding(null);
    };
    $[2] = adding;
    $[3] = setRate;
    $[4] = shop.id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const add = t2;
  const t3 = selected && "bg-primary/5";
  const t4 = expanded && "border-b-0";
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = cn(t3, t4);
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== canEdit || $[10] !== onSelect || $[11] !== selected || $[12] !== shop.name || $[13] !== t) {
    t6 = canEdit && <TableCell><input type="checkbox" className="size-4 accent-primary" aria-label={t("commissions.shops.select", {
        name: shop.name
      })} checked={selected} onChange={onSelect} /></TableCell>;
    $[9] = canEdit;
    $[10] = onSelect;
    $[11] = selected;
    $[12] = shop.name;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== expanded) {
    t7 = expanded ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />;
    $[15] = expanded;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onToggle || $[18] !== shop.name || $[19] !== t7) {
    t8 = <TableCell><button type="button" onClick={onToggle} className="flex items-center gap-1 text-left font-medium">{t7}{shop.name}</button></TableCell>;
    $[17] = onToggle;
    $[18] = shop.name;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = shop.inn ?? "\u2014";
  let t10;
  if ($[21] !== t9) {
    t10 = <TableCell className="text-muted-foreground tabular-nums">{t9}</TableCell>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = shop.status === "active" ? "secondary" : "destructive";
  const t12 = `commissions.status.${shop.status}`;
  let t13;
  if ($[23] !== shop.status || $[24] !== t || $[25] !== t12) {
    t13 = t(t12, {
      defaultValue: shop.status
    });
    $[23] = shop.status;
    $[24] = t;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <TableCell><Badge variant={t11}>{t13}</Badge></TableCell>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== shop.productCount) {
    t15 = <TableCell className="text-right tabular-nums">{shop.productCount}</TableCell>;
    $[30] = shop.productCount;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== shop.sales) {
    t16 = money(shop.sales);
    $[32] = shop.sales;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <TableCell className="text-right tabular-nums">{t16}</TableCell>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== shop.name || $[37] !== t) {
    t18 = t("commissions.shops.shopRateFor", {
      name: shop.name
    });
    $[36] = shop.name;
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = !canEdit;
  let t20;
  if ($[39] !== setRate || $[40] !== shop.id) {
    t20 = rate_0 => setRate.mutateAsync({
      shopId: shop.id,
      categoryId: null,
      rate: rate_0
    });
    $[39] = setRate;
    $[40] = shop.id;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== shop.shopRate || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <TableCell><RateCell label={t18} value={shop.shopRate} disabled={t19} onSave={t20} /></TableCell>;
    $[42] = shop.shopRate;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== shop.personalCount) {
    t22 = shop.personalCount > 0 ? shop.personalCount : <span className="text-muted-foreground">—</span>;
    $[47] = shop.personalCount;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t22) {
    t23 = <TableCell className="text-right tabular-nums">{t22}</TableCell>;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t10 || $[52] !== t14 || $[53] !== t15 || $[54] !== t17 || $[55] !== t21 || $[56] !== t23 || $[57] !== t5 || $[58] !== t6 || $[59] !== t8) {
    t24 = <TableRow className={t5}>{t6}{t8}{t10}{t14}{t15}{t17}{t21}{t23}</TableRow>;
    $[51] = t10;
    $[52] = t14;
    $[53] = t15;
    $[54] = t17;
    $[55] = t21;
    $[56] = t23;
    $[57] = t5;
    $[58] = t6;
    $[59] = t8;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== add || $[62] !== adding || $[63] !== canEdit || $[64] !== categories || $[65] !== categoryName || $[66] !== colSpan || $[67] !== expanded || $[68] !== own || $[69] !== setRate || $[70] !== shop.categories || $[71] !== shop.id || $[72] !== shop.name || $[73] !== t) {
    t25 = expanded && <TableRow className="hover:bg-transparent"><TableCell colSpan={colSpan} className="bg-muted/30 px-3 py-3 sm:px-6">{shop.categories.length === 0 ? <p className="text-sm text-muted-foreground">{t("commissions.shops.noCategories")}</p> : <Table><TableHeader><TableRow><TableHead>{t("commissions.shops.category")}</TableHead><TableHead className="text-right">{t("commissions.shops.products")}</TableHead><TableHead className="text-right">{t("commissions.shops.sales")}</TableHead><TableHead className="min-w-40">{t("commissions.categoryShops.personal")}</TableHead><TableHead>{t("commissions.shops.effective")}</TableHead></TableRow></TableHeader><TableBody>{shop.categories.map(row => <TableRow key={row.categoryId}><TableCell>{row.path}</TableCell><TableCell className="text-right tabular-nums">{row.productCount}</TableCell><TableCell className="text-right tabular-nums">{money(row.sales)}</TableCell><TableCell><RateCell label={t("commissions.categoryShops.rateFor", {
                  shop: shop.name,
                  category: row.name
                })} value={row.rate} disabled={!canEdit} onSave={rate_1 => setRate.mutateAsync({
                  shopId: shop.id,
                  categoryId: row.categoryId,
                  rate: rate_1
                })} /></TableCell><TableCell><div className="flex items-center gap-2"><span className="font-medium tabular-nums">{pct(row.effectiveRate)}</span><SourceBadge source={row.source} categoryName={row.sourceCategoryId ? categoryName.get(row.sourceCategoryId) : undefined} /></div></TableCell></TableRow>)}</TableBody></Table>}{canEdit && <div className="mt-2 flex flex-wrap items-center gap-2">{adding ? <><Select className="w-64" value={adding.categoryId} onChange={e => setAdding({
              ...adding,
              categoryId: e.target.value
            })}><option value="">{t("commissions.categories.choose")}</option>{categories.filter(row_0 => !own.has(row_0.id)).map(_temp15)}</Select><Input aria-label={t("commissions.categories.rate")} inputMode="decimal" className="h-9 w-20 text-right" placeholder="%" value={adding.pct} onChange={e_0 => setAdding({
              ...adding,
              pct: e_0.target.value
            })} onKeyDown={e_1 => e_1.key === "Enter" && void add()} /><Button size="sm" onClick={() => void add()}>{t("common.save", {
                defaultValue: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"
              })}</Button><Button size="sm" variant="ghost" onClick={() => setAdding(null)}>{t("common.cancel", {
                defaultValue: "\u041E\u0442\u043C\u0435\u043D\u0430"
              })}</Button></> : <Button size="xs" variant="outline" onClick={() => setAdding({
            categoryId: "",
            pct: ""
          })}>{t("commissions.shops.addPersonal")}</Button>}</div>}</TableCell></TableRow>;
    $[61] = add;
    $[62] = adding;
    $[63] = canEdit;
    $[64] = categories;
    $[65] = categoryName;
    $[66] = colSpan;
    $[67] = expanded;
    $[68] = own;
    $[69] = setRate;
    $[70] = shop.categories;
    $[71] = shop.id;
    $[72] = shop.name;
    $[73] = t;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t24 || $[76] !== t25) {
    t26 = <>{t24}{t25}</>;
    $[75] = t24;
    $[76] = t25;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  return t26;
}

/** Массовая ставка выбранным магазинам. */
function _temp15(row_1) {
  return <option key={row_1.id} value={row_1.id}>{`${"\u2014 ".repeat(row_1.depth)}${row_1.name}`}</option>;
}
function _temp14(c) {
  return c.categoryId;
}
function BulkBar({
  selected,
  names,
  categories,
  onClear
}: {
  selected: string[];
  names: Map<string, string>;
  categories: CommissionCategoryRow[];
  onClear: () => void;
}) {
  const {
    t
  } = useTranslation();
  const {
    bulk
  } = useCommissionMutations();
  const [categoryId, setCategoryId] = useState("");
  const [allCategories, setAllCategories] = useState(false);
  const [resetPersonal, setResetPersonal] = useState(true);
  const [text, setText] = useState("");
  const [message, setMessage] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);
  const apply = async (remove: boolean) => {
    const rate = remove ? null : parsePct(text);
    if (!remove && (rate == null || rate === undefined)) {
      setMessage({
        tone: "error",
        text: t("commissions.bulk.invalid")
      });
      return;
    }
    if (!allCategories && !categoryId) {
      setMessage({
        tone: "error",
        text: t("commissions.bulk.chooseCategory")
      });
      return;
    }
    try {
      const result = await bulk.mutateAsync({
        shopIds: selected,
        categoryId: allCategories ? null : categoryId,
        rate: rate ?? null,
        resetPersonal: allCategories && resetPersonal
      });
      setMessage({
        tone: "ok",
        text: t("commissions.bulk.done", {
          count: result.changed
        })
      });
    } catch (e) {
      setMessage({
        tone: "error",
        text: e instanceof Error ? e.message : String(e)
      });
    }
  };
  const shown = selected.slice(0, 3).map(id => names.get(id) ?? id).join(", ");
  return <div className="sticky top-2 z-10 flex flex-col gap-2 rounded-md border border-primary/30 bg-background p-3 shadow-sm">
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span className="font-medium">{t("commissions.bulk.selected", {
          count: selected.length
        })}</span>
				<span className="truncate text-muted-foreground">
					{shown}
					{selected.length > 3 ? "…" : ""}
				</span>
				<Button size="xs" variant="ghost" onClick={onClear}>
					{t("commissions.bulk.clear")}
				</Button>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<Select className="w-64" disabled={allCategories} value={categoryId} onChange={e_0 => setCategoryId(e_0.target.value)}>
					<option value="">{t("commissions.categories.choose")}</option>
					{categories.map(row => <option key={row.id} value={row.id}>
							{`${"— ".repeat(row.depth)}${row.name}`}
						</option>)}
				</Select>
				<label className="flex items-center gap-2 text-sm">
					<input type="checkbox" className="size-4 accent-primary" checked={allCategories} onChange={e_1 => setAllCategories(e_1.target.checked)} />
					{t("commissions.bulk.allCategories")}
				</label>
				<div className="relative">
					<Input aria-label={t("commissions.categories.rate")} inputMode="decimal" className="h-9 w-24 pr-6 text-right" value={text} onChange={e_2 => setText(e_2.target.value)} onKeyDown={e_3 => e_3.key === "Enter" && void apply(false)} />
					<span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-xs text-muted-foreground">
						%
					</span>
				</div>
				<Button size="sm" disabled={bulk.isPending} onClick={() => void apply(false)}>
					{bulk.isPending && <Loader2 className="animate-spin" />}
					{t("commissions.bulk.apply")}
				</Button>
				<Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void apply(true)}>
					{t("commissions.bulk.remove")}
				</Button>
			</div>
			{allCategories && <label className="flex items-center gap-2 text-xs text-muted-foreground">
					<input type="checkbox" className="size-4 accent-primary" checked={resetPersonal} onChange={e_4 => setResetPersonal(e_4.target.checked)} />
					{t("commissions.bulk.resetPersonal")}
				</label>}
			{message && <p className={cn("text-xs", message.tone === "error" ? "text-destructive" : "text-muted-foreground")}>
					{message.text}
				</p>}
		</div>;
}

// ── История ────────────────────────────────────────────────────────────────

function HistoryTable() {
  const $ = _c(46);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const history = useCommissionHistory(t0);
  if (!history.data) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loading />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (history.data.length === 0) {
    let t1;
    if ($[2] !== t) {
      t1 = t("commissions.history.empty");
      $[2] = t;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = <p className="text-sm text-muted-foreground">{t1}</p>;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== t) {
    t1 = row => {
      switch (row.scope) {
        case "base":
          {
            return t("commissions.history.base");
          }
        case "category":
          {
            return t("commissions.history.category", {
              category: row.categoryName ?? row.categoryId
            });
          }
        case "shop":
          {
            return t("commissions.history.shop", {
              shop: row.shopName ?? row.shopId
            });
          }
        default:
          {
            return t("commissions.history.pair", {
              shop: row.shopName ?? row.shopId,
              category: row.categoryName ?? row.categoryId
            });
          }
      }
    };
    $[6] = t;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const subject = t1;
  let t2;
  if ($[8] !== t) {
    t2 = t("commissions.history.when");
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <TableHead>{t2}</TableHead>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t) {
    t4 = t("commissions.history.who");
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <TableHead>{t4}</TableHead>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("commissions.history.what");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <TableHead>{t6}</TableHead>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("commissions.history.from");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <TableHead className="text-right">{t8}</TableHead>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t) {
    t10 = t("commissions.history.to");
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <TableHead className="text-right">{t10}</TableHead>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11 || $[29] !== t3 || $[30] !== t5 || $[31] !== t7 || $[32] !== t9) {
    t12 = <TableHeader><TableRow>{t3}{t5}{t7}{t9}{t11}</TableRow></TableHeader>;
    $[28] = t11;
    $[29] = t3;
    $[30] = t5;
    $[31] = t7;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== history.data || $[35] !== subject || $[36] !== t) {
    let t14;
    if ($[38] !== subject || $[39] !== t) {
      t14 = row_0 => <TableRow key={row_0.id}><TableCell className="whitespace-nowrap text-muted-foreground">{formatDateish(row_0.createdAt, {
            dateStyle: "short",
            timeStyle: "short"
          })}</TableCell><TableCell>{row_0.actorName || "\u2014"}</TableCell><TableCell>{subject(row_0)}</TableCell><TableCell className="text-right tabular-nums text-muted-foreground">{pct(row_0.fromRate)}</TableCell><TableCell className="text-right font-medium tabular-nums">{row_0.toRate == null ? t("commissions.history.removed") : pct(row_0.toRate)}</TableCell></TableRow>;
      $[38] = subject;
      $[39] = t;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    t13 = history.data.map(t14);
    $[34] = history.data;
    $[35] = subject;
    $[36] = t;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[41] !== t13) {
    t14 = <TableBody>{t13}</TableBody>;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t12 || $[44] !== t14) {
    t15 = <Table>{t12}{t14}</Table>;
    $[43] = t12;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}